import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FaCheck, FaStar, FaCrown } from 'react-icons/fa';

const PricingCards = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const plans = [
    {
      title: 'Mensal',
      price: 'R$139,90',
      period: '/mês',
      features: ['Acesso completo à academia', 'Aulas coletivas inclusas'],
      delay: '0s',
      popular: false
    },
    {
      title: 'Familiar',
      price: 'R$119,90',
      period: '/mês por pessoa',
      features: ['Acesso para família', 'Aulas coletivas inclusas', 'Desconto especial'],
      delay: '0.1s',
      popular: false
    },
    {
      title: 'Light',
      subtitle: '12h às 15h',
      price: 'R$109,90',
      period: '/mês',
      features: ['Horário especial', 'Aulas coletivas inclusas', 'Melhor custo-benefício'],
      delay: '0.2s',
      popular: false
    },
    {
      title: 'Trimestral',
      price: 'R$124,90',
      period: '/mês',
      features: ['3 meses de acesso', 'Aulas coletivas inclusas', 'Economia de 10%'],
      delay: '0.3s',
      popular: false
    },
    {
      title: 'Semestral',
      price: 'R$109,90',
      period: '/mês',
      features: ['6 meses de acesso', 'Aulas coletivas inclusas', 'Economia de 20%'],
      delay: '0.4s',
      popular: true
    },
    {
      title: 'Prime Anual',
      price: 'R$109,90',
      period: '/mês',
      features: ['12 meses de acesso', 'Aulas coletivas inclusas', 'Maior economia', 'Benefícios exclusivos'],
      delay: '0.5s',
      popular: false,
      premium: true
    }
  ];

  return (
    <Container id="precos" ref={sectionRef}>
      <BackgroundElements>
        <GlowOrb $position="left" />
        <GlowOrb $position="right" />
      </BackgroundElements>
      
      <Content>
        <HeaderSection $isVisible={isVisible}>
          <SectionBadge>
            <span>💰 INVESTIMENTO</span>
          </SectionBadge>
          <Title>
            ESCOLHA SEU <HighlightText>PLANO</HighlightText>
          </Title>
          <Subtitle>
            Invista em você. Todos os planos incluem acesso às aulas coletivas.
          </Subtitle>
        </HeaderSection>

        <CardsGrid>
          {plans.map((plan, index) => (
            <Card 
              key={index}
              $delay={plan.delay}
              $isVisible={isVisible}
              $popular={plan.popular}
              $premium={plan.premium}
            >
              {plan.popular && (
                <PopularBadge>
                  <FaStar /> MAIS POPULAR
                </PopularBadge>
              )}
              {plan.premium && (
                <PremiumBadge>
                  <FaCrown /> PREMIUM
                </PremiumBadge>
              )}
              
              <CardGlow />
              <CardContent>
                <PlanTitle>{plan.title}</PlanTitle>
                {plan.subtitle && <PlanSubtitle>{plan.subtitle}</PlanSubtitle>}
                
                <PriceContainer>
                  <Price>{plan.price}</Price>
                  <Period>{plan.period}</Period>
                </PriceContainer>
                
                <Divider />
                
                <FeaturesList>
                  {plan.features.map((feature, fIndex) => (
                    <FeatureItem key={fIndex}>
                      <CheckIcon><FaCheck /></CheckIcon>
                      <span>{feature}</span>
                    </FeatureItem>
                  ))}
                </FeaturesList>
                
                <CTAButton 
                  href="https://wa.me/5531971934978" 
                  target="_blank"
                  $popular={plan.popular}
                  $premium={plan.premium}
                >
                  <ButtonShine />
                  <span>QUERO ESSE</span>
                </CTAButton>
              </CardContent>
            </Card>
          ))}
        </CardsGrid>
      </Content>
    </Container>
  );
};

export default PricingCards;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const shine = keyframes`
  0% {
    left: -100%;
  }
  50%, 100% {
    left: 100%;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.1);
  }
`;

const Container = styled.section`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-dark);
  position: relative;
  padding: 60px 0;
  overflow: hidden;
`;

const BackgroundElements = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
`;

const GlowOrb = styled.div`
  position: absolute;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  filter: blur(100px);
  animation: ${pulse} 6s ease-in-out infinite;
  
  ${({ $position }) => $position === 'left' && css`
    left: -200px;
    top: 20%;
    background: rgba(229, 168, 0, 0.15);
  `}
  
  ${({ $position }) => $position === 'right' && css`
    right: -200px;
    bottom: 20%;
    background: rgba(229, 168, 0, 0.1);
    animation-delay: 3s;
  `}
`;

const Content = styled.div`
  width: 90%;
  max-width: 1300px;
  position: relative;
  z-index: 1;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
  opacity: 0;
  
  ${({ $isVisible }) => $isVisible && css`
    animation: ${fadeInUp} 0.8s ease forwards;
  `}
`;

const SectionBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: rgba(229, 168, 0, 0.1);
  border: 1px solid rgba(229, 168, 0, 0.2);
  border-radius: 50px;
  padding: 10px 25px;
  margin-bottom: 25px;

  span {
    font-family: 'Rajdhani', sans-serif;
    font-weight: 600;
    font-size: 0.85rem;
    color: var(--primary-color);
    letter-spacing: 3px;
  }
`;

const Title = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  color: #fff;
  margin-bottom: 15px;
`;

const HighlightText = styled.span`
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.1rem;
  color: #888;
  max-width: 500px;
  margin: 0 auto;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 25px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    max-width: 400px;
    margin: 0 auto;
  }
`;

const Card = styled.div`
  position: relative;
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 24px;
  overflow: hidden;
  opacity: 0;
  transition: all 0.4s ease;
  
  ${({ $isVisible, $delay }) => $isVisible && css`
    animation: ${scaleIn} 0.6s ease forwards;
    animation-delay: ${$delay};
  `}
  
  ${({ $popular }) => $popular && css`
    border-color: var(--primary-color);
    transform: scale(1.02);
    
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(180deg, rgba(229, 168, 0, 0.05) 0%, transparent 50%);
      pointer-events: none;
    }
  `}
  
  ${({ $premium }) => $premium && css`
    border-color: #ffd700;
    background: linear-gradient(180deg, rgba(255, 215, 0, 0.05) 0%, var(--glass-bg) 30%);
  `}

  &:hover {
    transform: translateY(-10px) ${({ $popular }) => $popular && 'scale(1.02)'};
    border-color: var(--primary-color);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3),
                0 0 30px var(--primary-glow);
  }
`;

const CardGlow = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 200px;
  height: 200px;
  background: var(--primary-color);
  filter: blur(100px);
  opacity: 0;
  transition: opacity 0.4s ease;
  pointer-events: none;
  
  ${Card}:hover & {
    opacity: 0.15;
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--gradient-primary);
  padding: 6px 14px;
  border-radius: 50px;
  font-family: 'Rajdhani', sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  color: #000;
  letter-spacing: 1px;
  z-index: 2;
  animation: ${float} 3s ease-in-out infinite;
`;

const PremiumBadge = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #ffd700 0%, #ff8c00 100%);
  padding: 6px 14px;
  border-radius: 50px;
  font-family: 'Rajdhani', sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  color: #000;
  letter-spacing: 1px;
  z-index: 2;
  animation: ${float} 3s ease-in-out infinite;
`;

const CardContent = styled.div`
  padding: 35px 30px;
  position: relative;
  z-index: 1;
`;

const PlanTitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.4rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 5px;
`;

const PlanSubtitle = styled.span`
  font-family: 'Rajdhani', sans-serif;
  font-size: 0.9rem;
  color: var(--primary-color);
  display: block;
  margin-bottom: 10px;
`;

const PriceContainer = styled.div`
  margin: 20px 0;
`;

const Price = styled.span`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.5rem;
  font-weight: 900;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Period = styled.span`
  font-family: 'Rajdhani', sans-serif;
  font-size: 0.9rem;
  color: #666;
  margin-left: 5px;
`;

const Divider = styled.div`
  width: 60px;
  height: 3px;
  background: var(--gradient-primary);
  border-radius: 2px;
  margin: 20px 0;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 30px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Rajdhani', sans-serif;
  font-size: 0.95rem;
  color: #aaa;
`;

const CheckIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: rgba(229, 168, 0, 0.1);
  border-radius: 50%;
  color: var(--primary-color);
  font-size: 0.7rem;
  flex-shrink: 0;
`;

const CTAButton = styled.a`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px;
  border-radius: 50px;
  font-family: 'Orbitron', sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  text-decoration: none;
  letter-spacing: 1px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  
  ${({ $popular, $premium }) => ($popular || $premium) ? css`
    background: var(--gradient-primary);
    color: #000;
    box-shadow: 0 10px 30px var(--primary-glow);
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 40px var(--primary-glow);
    }
  ` : css`
    background: transparent;
    border: 2px solid rgba(255, 255, 255, 0.2);
    color: #fff;
    
    &:hover {
      border-color: var(--primary-color);
      color: var(--primary-color);
      background: rgba(229, 168, 0, 0.05);
    }
  `}

  span {
    position: relative;
    z-index: 2;
  }
`;

const ButtonShine = styled.div`
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: ${shine} 3s ease-in-out infinite;
`;
